import { useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import useAuth from "../customHooks/useAuth";
import { useIsReadOnlyAdmin } from "../hooks/useIsReadOnlyAdmin";
import constant from "../constant";
import Header from "../components/common/header";
import Sidebar from "../components/common/Sidebar";
import Loader from "../components/common/loader/index";


/**
 * ✅ Safely extract meta info (convert any object/JSX to string)
 */
const getMetaInfo = (pathname) => {
    const allRoutes = {
        ...constant.Authedcomponent,
        ...constant.nonAuthedcomponent,
        ...constant.commoncomponent,
    };

    const route = Object.values(allRoutes).find((r) => r.url === pathname);

    const safeString = (val) =>
        typeof val === "string" ? val : val?.toString?.() || "Vessel Inspection Portal";

    return {
        title: safeString(route?.metaTitle || "Vessel Inspection Portal"),
        description: safeString(
            route?.metaDescription ||
            "Vessel Inspection and Crew Management Portal for maritime operations."
        ),
    };
};

/**
 * ✅ PrivateRoute (only for logged-in users)
 */
export const PrivateRoute = () => {
    const { isAuthenticated, loading } = useAuth();
    const location = useLocation();
    const meta = getMetaInfo(location.pathname);

    if (loading) return <Loader/>
    if (!isAuthenticated) {
        return <Navigate to={constant.nonAuthedcomponent.login.url} replace />;
    }
    return (
        <div id="layout-wrapper">
            <Helmet>
                <title>{meta.title}</title>
                <meta name="description" content={meta.description} />
            </Helmet>

            <Header />
            <Sidebar />
            <div className="main-content">
                <Outlet />
            </div>
        </div>
    );
};

/**
 * ✅ ByPassPrivateRoute
 */
export const ByPassPrivateRoute = ({ hideTopbard, hideHeader, hideFooter }) => {
    const location = useLocation();
    const meta = getMetaInfo(location.pathname);

    return (
        <div id="layout-wrapper">
            <Helmet>
                <title>{meta.title}</title>
                <meta name="description" content={meta.description} />
            </Helmet>

            {!hideHeader && <Header />}
            <Sidebar />
            <div className="main-content">
                <Outlet />
            </div>
        </div>
    );
};

/**
 * ✅ NonAuthedRoute
 */
export const NonAuthedRoute = () => {
    const { isAuthenticated, loading } = useAuth();
    const location = useLocation();
    const meta = getMetaInfo(location.pathname);

    if (loading) return null;

    return !isAuthenticated ? (
        <>
            <Helmet>
                <title>{meta.title}</title>
                <meta name="description" content={meta.description} />
            </Helmet>
            <Outlet />
        </>
    ) : (
        <Navigate to={constant.Authedcomponent.dashboard.url} replace />
    );
};

/**
 * ✅ CrewOnlyRoute
 *
 * Outlet guard that blocks Super Admin (read-only role) from reaching any
 * data-entry / creation route. Crew + master / officer roles pass through.
 * Super Admin is bounced to the dashboard with a toast so they understand
 * why the navigation didn't land.
 */
export const CrewOnlyRoute = ({ redirectTo = constant.Authedcomponent.dashboard.url } = {}) => {
    const { isReadOnlyAdmin, loading } = useIsReadOnlyAdmin();
    const toastedRef = useRef(false);

    useEffect(() => {
        if (!loading && isReadOnlyAdmin && !toastedRef.current) {
            toastedRef.current = true;
            toast.info("Super Admin is read-only — entries are submitted by vessel crew.");
        }
    }, [loading, isReadOnlyAdmin]);

    if (loading) return null;
    if (isReadOnlyAdmin) return <Navigate to={redirectTo} replace />;
    return <Outlet />;
};

/**
 * ✅ SuperAdminOnlyRoute
 *
 * Outlet guard that allows ONLY Super Admin through. Other roles (crew,
 * master, owner, etc.) are bounced to the dashboard with a toast so they
 * understand why the navigation didn't land. Used for routes that are
 * configured centrally by the shore-side super admin — e.g. Voyage Route
 * Plan.
 */
export const SuperAdminOnlyRoute = ({ redirectTo = constant.Authedcomponent.dashboard.url } = {}) => {
    const { isReadOnlyAdmin, loading } = useIsReadOnlyAdmin();
    const toastedRef = useRef(false);

    useEffect(() => {
        if (!loading && !isReadOnlyAdmin && !toastedRef.current) {
            toastedRef.current = true;
            toast.info("Only Super Admin can edit the Voyage Route Plan.");
        }
    }, [loading, isReadOnlyAdmin]);

    if (loading) return null;
    if (!isReadOnlyAdmin) return <Navigate to={redirectTo} replace />;
    return <Outlet />;
};

/**
 * ✅ CommonRoute
 */
export const CommonRoute = () => {
    const location = useLocation();
    const meta = getMetaInfo(location.pathname);

    return (
        <>
            <Helmet>
                <title>{meta.title}</title>
                <meta name="description" content={meta.description} />
            </Helmet>
            <Outlet />
        </>
    );
};
